/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VehicleFunction.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import deleteIcon from "../../assets/delete_icon.png";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import customConsole from "../../config/customConsole";
import { HandleApiActions } from "../../redux/actions/actionHandler";
import profileActions from "../../redux/actions/profile";
import adminSettingsAPI from "../../redux/actions/adminSetttingAPI";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import { DialogContentText, IconButton } from "@mui/material";
import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomDataGridV2 from "../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import * as XLSX from "xlsx";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import PropTypes from "prop-types";

const getVehicleFunctionList = (resp) => {
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.response?.data)) return resp.response.data;
  return [];
};

function VehicleFunction({ open, handleClick }) {
  const [tableKey, setTableKey] = useState(0);
  const [totalVehicleFunctionCount, setTotalVehicleFunctionCount] = useState(0);

  useEffect(() => {
    if (open === "vehicle-function") {
      setTableKey((prev) => prev + 1);
    }
  }, [open]);

  const dispatch = useDispatch();
  const vehicleFunctionDetails = useSelector(
    (state) => state.vehicleFunctionReducer,
  );
  const timeZoneDetails = useSelector((state) => state.profileReducer);

  const [page, setPage] = React.useState(1); // Changed to 1-based pagination like VehicleClass
  const [rowsPerPage] = React.useState(25);
  const [getVehicleFunction, setGetVehicleFunction] = React.useState([]);
  const [openAddVehicleFunctionDialog, setOpenAddVehicleFunctionDialog] =
    React.useState(false);
  const [openEditVehicleFunctionDialog, setOpenEditVehicleFunctionDialog] =
    React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [openDeleteVehicleFunctionDialog, setOpenDeleteVehicleFunctionDialog] =
    React.useState(false);
  const [tableRowsData, setTableRowsData] = useState([]);
  const [searchQuery] = useState("");

  const COLUMNS = [
    {
      field: "sno",
      headerName: "S.No",
      flex: 0.3,
      minWidth: 60,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 0.8,
      minWidth: 120,
    },
    {
      field: "abbreviation",
      headerName: "Abbreviation",
      flex: 0.5,
      minWidth: 80,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 0.5,
      minWidth: 80,
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 0.6,
      minWidth: 100,
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      flex: 0.6,
      minWidth: 100,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.4,
      minWidth: 90,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            sx={{ color: "#000000", p: 0.5 }}
            onClick={(e) => {
              e.stopPropagation();
              handleTableRowClick(params, false);
            }}
          >
            <BorderColorIcon fontSize="inherit" sx={{ fontSize: 19 }} />
          </IconButton>
          <IconButton
            size="small"
            sx={{ p: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              handleTableRowClick(params, true);
            }}
          >
            <img
              src={deleteIcon}
              alt="delete"
              style={{ width: 24, height: 24 }}
            />
          </IconButton>
        </Box>
      ),
    },
  ];

  const supplierRef = useRef(null);
  useEffect(() => {
    if (open === "4") {
      const timeout = setTimeout(() => {
        if (
          supplierRef.current &&
          supplierRef.current.scrollHeight > supplierRef.current.clientHeight
        ) {
          supplierRef.current.scrollTop = supplierRef.current.scrollHeight;
        }
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  useEffect(() => {
    customConsole(getVehicleFunction);
    let filteredUsersList = getVehicleFunction?.map((data, index) => {
      return {
        ...data,
        sno: (page - 1) * rowsPerPage + index + 1, // Calculate serial number based on pagination
        id: data?.id,
        abbreviation: data?.abbreviation,
        name: data?.name,
        description: data?.other_fields?.description,
        created_at: convertUtcToTimeZone(
          data?.created_at,
          timeZoneDetails?.profile?.profile?.region,
        ),
        updated_at: convertUtcToTimeZone(
          data?.updated_at,
          timeZoneDetails?.profile?.profile?.region,
        ),
      };
    });

    setTableRowsData(filteredUsersList || []); // Ensure it's always an array
  }, [
    getVehicleFunction,
    page,
    rowsPerPage,
    timeZoneDetails?.profile?.profile?.region,
  ]);

  React.useEffect(() => {
    dispatch(HandleApiActions({ ...profileActions.get_coutry_regions }));
  }, [dispatch]);
  React.useEffect(() => {
    customConsole("timeZoneDetails?.data?.profile?.region");
    customConsole(timeZoneDetails?.profile?.profile?.region);
  }, [timeZoneDetails]);

  const filteredRows = searchQuery
    ? tableRowsData.filter((row) =>
        COLUMNS.some((column) =>
          row[column.field]
            ?.toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase()),
        ),
      )
    : tableRowsData;
  const toggleDialog = (newOpen) => () => {
    setOpenAddVehicleFunctionDialog(newOpen);
  };

  const handleAddVehicleFunctionClick = () => {
    toggleDialog(true)();
  };

  const handleTableRowClick = (params, isButtonClicked) => {
    setSelectedRow(params);
    customConsole(params);
    if (isButtonClicked) {
      setOpenDeleteVehicleFunctionDialog(true);
    } else {
      setOpenEditVehicleFunctionDialog(true);
    }
  };

  const getVehicleFunctionDetails = useCallback(
    async (pageOverride) => {
      const pageToUse = pageOverride ?? page;
      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_vehicle_function,
          show_toast: false,
          params: {
            page_number: pageToUse,
            page_size: rowsPerPage,
          },
        }),
      );

      if (resp?.error) {
        setGetVehicleFunction([]);
        setTableRowsData([]);
        setTotalVehicleFunctionCount(0);
        return { list: [], totalCount: 0 };
      }

      const list = getVehicleFunctionList(resp);

      const totalCount =
        resp?.total_count ??
        resp?.totalItems ??
        resp?.total ??
        (Array.isArray(list) ? list.length : 0);

      setGetVehicleFunction(list);
      setTotalVehicleFunctionCount(totalCount);

      return { list, totalCount };
    },
    [dispatch, page, rowsPerPage],
  );

  useEffect(() => {
    getVehicleFunctionDetails();
  }, [page, rowsPerPage, getVehicleFunctionDetails]);

  React.useEffect(() => {
    customConsole("VehicleFunction Train Details...................");
    customConsole(vehicleFunctionDetails);
  }, [vehicleFunctionDetails]);

  useEffect(() => {
    setPage(1); // Reset to first page when search changes
  }, [searchQuery]);

  customConsole("GetVehicleFunction...............,", getVehicleFunction);

  const handleExportToExcel = () => {
    if (!filteredRows || filteredRows.length === 0) {
      console.warn("No data to export");
      return;
    }

    const exportData = filteredRows.map((row) => ({
      "S.No": row.sno,
      Name: row.name,
      Abbreviation: row.abbreviation,
      Created: row.created_at,
      Updated: row.updated_at,
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    XLSX.utils.book_append_sheet(wb, ws, "Vehicle Functions");

    XLSX.writeFile(wb, "Vehicle_Functions.xlsx");
  };

  const breadcrumbItems = [
    { label: "Settings" },
    { label: "Feature Portfolio" },
    { label: "Vehicle Function" },
  ];

  return (
    <Box
      sx={{
        margin: 0,
        padding: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F5F5F5",
      }}
    >
      <Box
        sx={{
          padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
          flexShrink: 0,
        }}
      >
        <CommonBreadcrumbs items={breadcrumbItems} />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: { xs: "0 16px 12px 16px", md: "0 24px 16px 24px" },
          flexShrink: 0,
          mt: 1,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <Box>
            <CustomSearchbarWithApiV2
              params={{
                page_number: page,
                page_size: rowsPerPage,
              }}
              apiAction={adminSettingsAPI.get_vehicle_function}
              searchKey={"name"}
              getDataOnSearch={(data, resp) => {
                console.log("Search Data:", resp);
                if (resp?.error) {
                  setGetVehicleFunction([]);
                  setTableRowsData([]);
                  setTotalVehicleFunctionCount(0);
                  return;
                }

                const searchedList = Array.isArray(data) ? data : [];
                setGetVehicleFunction(searchedList);
                setTotalVehicleFunctionCount(
                  resp?.total_count ??
                    resp?.totalItems ??
                    resp?.total ??
                    searchedList.length,
                );
              }}
              placeholder={"Search"}
            />
          </Box>
          <Box>
            <AddButtonV2 handleOnClick={handleAddVehicleFunctionClick} />
          </Box>
        </Box>
        <Box>
          <CustomButtonV2
            startIcon={<FileDownloadOutlinedIcon />}
            type="submit"
            size="small"
            btnName="Export"
            handleOnClick={handleExportToExcel}
          />
        </Box>
      </Box>

      <Box
        sx={{
          height: "76vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          padding: { xs: "0 8px", md: "0 16px" },
          mt: -1,
        }}
        key={`vehicle-function-table-${tableKey}`}
      >
        <CustomDataGridV2
          rows={filteredRows}
          columns={COLUMNS}
          isMultiLine={true}
          onRowClick={(params) => handleTableRowClick(params.row, false)}
          showPagination={true}
          page={page}
          rowsPerPage={rowsPerPage}
          handlePageChange={(newPage) => setPage(newPage)}
          totalItems={totalVehicleFunctionCount}
        />
      </Box>

      <AddVehicleFunction
        open={openAddVehicleFunctionDialog}
        toggleDialog={toggleDialog}
        setPage={setPage}
        refreshVehicleFunction={getVehicleFunctionDetails}
      />
      <EditVehicleFunction
        open={openEditVehicleFunctionDialog}
        setOpenEditVehicleFunctionDialog={setOpenEditVehicleFunctionDialog}
        selectedRow={selectedRow}
        page={page}
        refreshVehicleFunction={getVehicleFunctionDetails}
      />

      <DeleteVehicleFunction
        openDeleteVehicleFunctionDialog={openDeleteVehicleFunctionDialog}
        setOpenDeleteVehicleFunctionDialog={setOpenDeleteVehicleFunctionDialog}
        selectedRow={selectedRow}
        setPage={setPage}
        page={page}
        rowsPerPage={rowsPerPage}
        refreshVehicleFunction={getVehicleFunctionDetails}
      />
    </Box>
  );
}

export default VehicleFunction;

const selectedVehicleFunctionRow = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  name: PropTypes.string,
  abbreviation: PropTypes.string,
  description: PropTypes.string,
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    abbreviation: PropTypes.string,
    other_fields: PropTypes.shape({
      description: PropTypes.string,
    }),
  }),
});

VehicleFunction.propTypes = {
  open: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleClick: PropTypes.func,
};

const AddVehicleFunction = ({
  open,
  toggleDialog,
  setPage,
  refreshVehicleFunction,
}) => {
  const dispatch = useDispatch();

  const [name, setName] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const [description, setDescription] = useState("");

  const handleClose = () => {
    setName("");
    setAbbreviation("");
    setDescription("");
    toggleDialog(false)();
  };

  const handleClickAdd = async () => {
    try {
      const reqObj = {
        current_time: new Date(),
        abbreviation: abbreviation,
        name: name,
      };

      if (description?.trim()) {
        reqObj.description = description;
      }
      await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.add_vehicle_function,
          params: reqObj,
          show_toast: true,
        }),
      );
      toggleDialog(false)();

      setPage(1);
      await refreshVehicleFunction(1);
      setName("");
      setDescription("");
      setAbbreviation("");
    } catch (error) {
      console.error("Error adding vehicle function:", error);
    }
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title={"Add Vehicle Function"}
      successButtonName={"Add"}
      rejectButtonName={"Close"}
      onClickSuccess={handleClickAdd}
      onDisabled={
        !(
          name?.length > 2 &&
          name?.trim() !== "" &&
          abbreviation?.length > 1 &&
          abbreviation?.trim() !== ""
        )
      }
      actionButtonStyle={"continuous"}
      onClickReject={handleClose}
    >
      <Stack spacing={2} sx={{ width: "350px" }}>
        <CustomFormTextFieldV2
          label="Name"
          required={true}
          value={name}
          setValue={(val) => {
            if (val.length <= 35) {
              setName(val);
            }
          }}
          maxLength={35}
        />
        <CustomFormTextFieldV2
          label="Abbreviation"
          required={true}
          value={abbreviation}
          setValue={(val) => {
            if (val.length <= 35) {
              setAbbreviation(val);
            }
          }}
          fldType="text1"
          maxLength={35}
        />
        <CustomFormTextFieldV2
          label="Description"
          value={description}
          setValue={(val) => {
            if (val.length <= 250) {
              setDescription(val);
            }
          }}
          fldType="notext"
          maxLength={250}
        />
      </Stack>
    </CustomDialogBoxV2>
  );
};

AddVehicleFunction.propTypes = {
  open: PropTypes.bool,
  toggleDialog: PropTypes.func,
  setPage: PropTypes.func,
  refreshVehicleFunction: PropTypes.func,
};

const EditVehicleFunction = ({
  open,
  setOpenEditVehicleFunctionDialog,
  selectedRow,
  page,
  refreshVehicleFunction,
}) => {
  const dispatch = useDispatch();
  customConsole("selectedRow");

  customConsole(selectedRow);

  const [editName, setEditName] = useState("");
  const [editAbbreviation, setEditAbbreviation] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [initialDescription, setInitialDescription] = useState("");

  React.useEffect(() => {
    if (selectedRow) {
      console.log("selectedRow in EditVehicleFunction:", selectedRow);

      const rowData = selectedRow.row || selectedRow;

      setEditName(rowData?.name || "");
      setEditAbbreviation(rowData?.abbreviation || "");
      const desc = rowData?.other_fields?.description || "";
      setEditDescription(desc);
      setInitialDescription(desc);
    }
  }, [selectedRow]);

  const normalize = (val) => String(val ?? "").trim();
  const isDescriptionValid = normalize(editDescription).length >= 3;
  const isDescriptionChanged =
    normalize(editDescription) !== normalize(initialDescription);
  React.useEffect(() => {
    customConsole("selectedRow");
    customConsole(selectedRow);
    customConsole("name");
    customConsole(selectedRow?.row?.name || selectedRow?.name);
  }, [selectedRow]);

  const handleClose = () => {
    setOpenEditVehicleFunctionDialog(false);
  };

  const handleClickEdit = async () => {
    try {
      const rowData = selectedRow?.row || selectedRow;

      const reqObj = {
        current_time: new Date(),
        id: rowData?.id,
        description: editDescription,
      };

      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.update_vehicle_function,
          params: reqObj,
          show_toast: true,
        }),
      );
      if (!resp?.error) {
        setOpenEditVehicleFunctionDialog(false);
      }

      await refreshVehicleFunction(page);
    } catch (error) {
      console.error("Error updating vehicle function:", error);
    }
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title={"Update Vehicle Function"}
      successButtonName={"Update"}
      rejectButtonName={"Close"}
      onClickSuccess={handleClickEdit}
      onDisabled={!(isDescriptionValid && isDescriptionChanged)}
      actionButtonStyle={"continuous"}
      onClickReject={handleClose}
    >
      <Stack spacing={2} sx={{ width: "350px" }}>
        <CustomFormTextFieldV2
          label="Name"
          value={editName}
          setValue={(val) => {
            if (val.length <= 35) {
              setEditName(val);
            }
          }}
          disabled={true}
          required={true}
        />
        <CustomFormTextFieldV2
          label={"Abbreviation"}
          value={editAbbreviation}
          setValue={(val) => {
            if (val.length <= 35) {
              setEditAbbreviation(val);
            }
          }}
          disabled={true}
          required={true}
        />
        <CustomFormTextFieldV2
          label={"Description"}
          value={editDescription}
          setValue={(val) => {
            if (val.length <= 250) {
              setEditDescription(val);
            }
          }}
          maxLength={250}
        />
      </Stack>
    </CustomDialogBoxV2>
  );
};

EditVehicleFunction.propTypes = {
  open: PropTypes.bool,
  setOpenEditVehicleFunctionDialog: PropTypes.func,
  selectedRow: selectedVehicleFunctionRow,
  page: PropTypes.number,
  refreshVehicleFunction: PropTypes.func,
};

const DeleteVehicleFunction = ({
  openDeleteVehicleFunctionDialog,
  selectedRow,
  setOpenDeleteVehicleFunctionDialog,
  setPage,
  page,
  rowsPerPage,
  refreshVehicleFunction,
}) => {
  const dispatch = useDispatch();

  const handleDelete = async () => {
    try {
      const rowData = selectedRow?.row || selectedRow;

      const reqObj = {
        current_time: new Date(),
        id: rowData?.id,
      };

      await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.delete_vehicle_function,
          params: reqObj,
          show_toast: true,
        }),
      );
      setOpenDeleteVehicleFunctionDialog(false);

      const refreshResult = await refreshVehicleFunction(page);
      const totalCount = refreshResult?.totalCount ?? 0;
      if (page > 1 && totalCount <= (page - 1) * rowsPerPage) {
        const newPage = page - 1;
        setPage(newPage);
        await refreshVehicleFunction(newPage);
      }
    } catch (error) {
      console.error("Error deleting vehicle function:", error);
    }
  };
  return (
    <CustomDialogBoxV2
      open={openDeleteVehicleFunctionDialog}
      onClose={() => setOpenDeleteVehicleFunctionDialog(false)}
      title={"Delete Vehicle Function"}
      successButtonName={"Ok"}
      rejectButtonName={"Cancel"}
      onClickSuccess={handleDelete}
      onClickReject={() => setOpenDeleteVehicleFunctionDialog(false)}
      actionButtonStyle={"continuous"}
    >
      <Box
        sx={{
          width: "350px",
          height: "50px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <DialogContentText>Are you sure want you to delete?</DialogContentText>
      </Box>
    </CustomDialogBoxV2>
  );
};

DeleteVehicleFunction.propTypes = {
  openDeleteVehicleFunctionDialog: PropTypes.bool,
  selectedRow: selectedVehicleFunctionRow,
  setOpenDeleteVehicleFunctionDialog: PropTypes.func,
  setPage: PropTypes.func,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  refreshVehicleFunction: PropTypes.func,
};
